import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Spacer } from '@components/Spacer';
import { REGISTRATION_STORAGE_KEY } from '@account_module/constants/registrationCtxKeys';
import { useUser } from '@auth_module/hooks/useUser';
import { useCustomerContext } from '@account_module/context/CustomerContext';

import { CloseIcon } from '@icons/CloseIcon';
import { WaveIcon } from '@icons/WaveIcon';
import { CreditCardsIcon } from '@icons/CreditCardsIcon';
import { DocumentsIcon } from '@icons/DocumentsIcon';

const Centered = tw.div`w-full h-full flex flex-col items-center justify-center`;
const CardsContainer = tw.div`flex gap-[24px] flex-col min-w-[330px] `;
const Card = tw.div`border border-solid border-gray-3 shadow-ui p-[16px] rounded-[12px] w-full max-w-[330px] flex flex-col items-center justify-between min-h-[185px] bg-neutral-background`;
const CardHeader = tw.div`w-full flex gap-[12px] items-center mb-[8px]`;
const CardTopContent = tw.div`w-full`;
const CardBottomContent = tw.div`w-full flex items-end gap-1`;
const Title = tw.h5`text-[18px] leading-[140%] font-bold text-dark-accent-invert`;
const StyledEmoji = tw.span`text-[32px] leading-none font-bold`;
const XlStyledEmoji = tw.span`text-[56px] text-center leading-none font-bold block w-auto mx-auto`;
const StyledP = tw.p`text-left text-[14px] leading-[130%] font-normal tracking-[0.14px] text-dark-accent-invert`;
const StyledLink = tw.a`underline text-accent-1 text-center block`;
// const CardLabel = tw.div`w-auto px-[8px] py-[4px] rounded-[4px] border border-solid whitespace-nowrap`;
// const BoldText = tw.div`text-text-and-icon text-[24px] font-bold leading-[140%] tracking-[0.24px]`;
// const BalanceContainer = tw.div`opacity-40 text-left`;
// const infoLabelStyles = tw`border-info-text-and-icon text-info-text-and-icon bg-info-bg` as TwStyle;
// const portalButtonStyles =
//   tw`bg-info-text-and-icon text-neutral-background w-full h-[34px] whitespace-nowrap` as TwStyle;
// const successLabelStyles =
//   tw`border-success-icon-bg text-success-text-and-icon bg-success-bg` as TwStyle;

export const ApiKeysBilling = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { customer } = useCustomerContext();
  const localStorageGreeting = window.localStorage.getItem(
    REGISTRATION_STORAGE_KEY.SELF_SERVICE_GREETING
  );
  const [shouldDisplayGreetingCard, setShouldDisplayGreatingCard] = useState<boolean | null>(
    localStorageGreeting && JSON.parse(localStorageGreeting)
  );

  const handleDismissGreeting = () => {
    setShouldDisplayGreatingCard(false);
    window.localStorage.removeItem(REGISTRATION_STORAGE_KEY.SELF_SERVICE_GREETING);
  };

  return (
    <CardsContainer>
      {!!shouldDisplayGreetingCard && (
        <Card tw='min-h-[160px]'>
          <CardTopContent>
            <CardHeader tw='justify-between'>
              <StyledEmoji>
                <WaveIcon />
              </StyledEmoji>
              <button onClick={handleDismissGreeting}>
                <CloseIcon tw='fill-text-and-icon' stroke='none' />
              </button>
            </CardHeader>
          </CardTopContent>
          <div>
            <Title>
              {t(
                'creator_dashboard_module.self_serve_api_page.api_key_billing.cards.welcome.title',
                { name: user?.first_name }
              )}
            </Title>
            <Spacer direction='vertical' size='xs' />
            <StyledP>
              {t(
                'creator_dashboard_module.self_serve_api_page.api_key_billing.cards.welcome.description'
              )}
            </StyledP>
          </div>
        </Card>
      )}

      {!customer?.pm_last_four && (
        <Card tw='border-none bg-info-bg'>
          <CardTopContent>
            <Title>
              {t(
                'creator_dashboard_module.self_serve_api_page.api_key_billing.cards.add_payment_method.title'
              )}
            </Title>
            <Spacer direction='vertical' size='md' />
            <StyledP tw='text-[16px] font-bold mb-[4px]'>
              {t(
                'creator_dashboard_module.self_serve_api_page.api_key_billing.cards.add_payment_method.price_info'
              )}
            </StyledP>
            <StyledP tw='tracking-normal'>
              {t(
                'creator_dashboard_module.self_serve_api_page.api_key_billing.cards.add_payment_method.description'
              )}
            </StyledP>
            <Spacer direction='vertical' size='sm' />
          </CardTopContent>
        </Card>
      )}
      <Card>
        <CardHeader tw='flex-col'>
          <StyledEmoji>
            <CreditCardsIcon />
          </StyledEmoji>
          <Title>
            {t('creator_dashboard_module.self_serve_api_page.api_key_billing.cards.invoice.title')}
          </Title>
        </CardHeader>
        <CardBottomContent tw='justify-end'></CardBottomContent>
      </Card>
      <Card>
        <Centered>
          <XlStyledEmoji>
            <DocumentsIcon />
          </XlStyledEmoji>
          <Spacer direction='vertical' size='md' />
          <StyledLink href='https://api-documentation.sounder.fm' target='_blank'>
            {t('creator_dashboard_module.self_serve_api_page.api_key_billing.cards.docs.title')}
          </StyledLink>
        </Centered>
      </Card>
    </CardsContainer>
  );
};
